import React, { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Footer } from './components/Footer';
import { Header } from './components/Header';
import { HomeHero } from './components/HomeHero';
import { AuthProvider } from './context/AuthContext';
import { I18nProvider, useI18n } from './i18n/I18nContext';
import { HomePage } from './pages/HomePage';
import { MovieDetailPage } from './pages/MovieDetailPage';
import { PaymentSuccessPage } from './pages/PaymentSuccessPage';
import { PlaybackPage } from './pages/PlaybackPage';
import { hasRegionBlocked, subscribeToRegionBlocked } from './services/api';

const RegionBlockBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useI18n();
  const [blocked, setBlocked] = useState(() => hasRegionBlocked());

  useEffect(() => {
    return subscribeToRegionBlocked(() => setBlocked(true));
  }, []);

  if (blocked) {
    return (
      <main>
        <HomeHero />
        <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="rounded-lg border border-stone-700 bg-stone-900 p-6 text-center shadow-sm">
            <h1 className="text-3xl font-bold text-white">{t('regionUnavailableTitle')}</h1>
            <p className="mt-4 text-base leading-7 text-stone-300">{t('regionUnavailableMessage')}</p>
          </div>
        </section>
      </main>
    );
  }

  return <>{children}</>;
};

function App() {
  return (
    <HelmetProvider>
      <I18nProvider>
        <BrowserRouter>
          <AuthProvider>
            <Header />
            <RegionBlockBoundary>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/login" element={<Navigate to="/" replace />} />
                <Route path="/signup" element={<Navigate to="/" replace />} />
                <Route path="/auth/callback" element={<Navigate to="/" replace />} />
                <Route path="/payment/success" element={<PaymentSuccessPage />} />
                <Route path="/movies/:id" element={<MovieDetailPage />} />
                <Route path="/watch/:id" element={<PlaybackPage />} />
                <Route path="/guest-watch/:accessToken" element={<PlaybackPage />} />
                <Route path="/rentals" element={<Navigate to="/" replace />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </RegionBlockBoundary>
            <Footer />
          </AuthProvider>
        </BrowserRouter>
      </I18nProvider>
    </HelmetProvider>
  );
}

export default App;
